import { internalMetricsEnabled } from "@/lib/tracking/track";
import type { SiteSection, TransitionSource } from "@/lib/tracking/sections";
import type { VisitSource } from "@/lib/tracking/visit-source";
import type { FunnelGame, PromoPlacement } from "@/lib/tracking/game-funnel";

const API = (process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3001/api").replace(/\/$/, "");

// Antes esta función usaba navigator.sendBeacon() para las llamadas sin body
// (click/cardClick/view) y fetch+keepalive solo para las que sí llevan body
// (impression/time) — sendBeacon no soporta headers/JSON con preflight, de
// ahí la rama distinta. Se unificó todo a fetch+keepalive (2026-08-19):
// sendBeacon es un objetivo común de ad-blockers/extensiones de privacidad
// (se identifica como "beacon de tracking" más fácil que un fetch normal) y
// no expone ningún resultado para depurar — con datos reales se detectó que
// los clics en "Ver evento" casi nunca se registraban mientras impressions
// (ya en fetch+keepalive) sí. Un solo mecanismo, comprobadamente confiable.
// Desde 2026-10-23 todas pasan por internalMetricsEnabled(): ni el equipo,
// ni Staging, ni navegadores automatizados suman a las estadísticas (antes
// solo las visitas por país lo respetaban).
function beacon(url: string, body?: object): void {
  if (typeof window === "undefined" || !internalMetricsEnabled()) return;
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
    keepalive: true,
  }).catch(() => {});
}

export function trackClick(slug: string): void {
  beacon(`${API}/analytics/click/${slug}`);
}

export function trackCardClick(slug: string): void {
  beacon(`${API}/analytics/card-click/${slug}`);
}

export function trackView(slug: string): void {
  beacon(`${API}/analytics/view/${slug}`);
}

export function trackImpressions(slugs: string[]): void {
  if (!slugs.length) return;
  beacon(`${API}/analytics/impression`, { slugs });
}

export function trackTime(slug: string, ms: number): void {
  if (ms <= 0) return;
  beacon(`${API}/analytics/time/${slug}`, { ms });
}

// Visitas por país (ver lib/tracking/site-visit.ts).
// returning/firstOfDay/firstOfWeek/firstSeen los decide el navegador con
// fechas guardadas en él; source = de dónde llegó la pestaña.
export interface VisitFlags {
  returning?: boolean;
  firstOfDay?: boolean;
  firstOfWeek?: boolean;
  firstSeen?: string;
  source?: VisitSource;
}

export function trackSiteVisit(flags: VisitFlags): void {
  beacon(`${API}/analytics/visit`, { platform: "web", ...flags });
}

export function trackSiteVisitTime(ms: number): void {
  beacon(`${API}/analytics/visit-time`, { platform: "web", ms: Math.round(ms) });
}

// Secciones y embudo hacia eventos (ver lib/tracking/sections.ts).
export function trackPage(
  section: SiteSection,
  newSession: boolean,
  toEventFrom?: SiteSection[],
  from?: TransitionSource,
): void {
  beacon(`${API}/analytics/page`, {
    platform: "web",
    section,
    newSession,
    ...(toEventFrom?.length && { toEventFrom }),
    ...(from && { from }),
  });
}

// Embudo de juegos (ver lib/tracking/game-funnel.ts).
export function trackGameFunnel(
  game: FunnelGame,
  step: "promo_view" | "promo_click" | "player_first" | "player_yesterday" | "player_earlier" | "share" | "share_visit",
  placement?: PromoPlacement,
): void {
  beacon(`${API}/analytics/game-funnel`, { platform: "web", game, step, ...(placement && { placement }) });
}
